import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { UserManager } from 'oidc-client-ts';
import { Suspense, useEffect, type ReactElement } from 'react';
import { RouterProvider, createBrowserRouter, useRouteError } from 'react-router';

import { AideBouton } from './aide/index.js';
import { SessionProvider, useSession } from './auth.js';
import {
  Button,
  ButtonLink,
  EmptyState,
  ErrorMessage,
  Loading,
  PageHeader,
} from './design/components.js';
import { GestionHome, Home } from './home/Home.js';
import { echecChargementMorceau, recharger } from './miseajour/enregistrement.js';
import { estEchecChargementMorceau } from './miseajour/logique.js';
import { MODULE_WEB } from './modules/registry.js';
import { HealthPage } from './pages/HealthPage.js';
import { MaintenancePage } from './pages/MaintenancePage.js';
import { ModulesPage } from './pages/ModulesPage.js';
import { AccountPage } from './pages/account/AccountPage.js';
import { WebPushSection } from './pages/account/WebPushSection.js';
import { ApiRequestError, type RuntimeConfig } from './runtime.js';
import { DomainPage } from './shell/DomainPage.js';
import { MonComptePage } from './shell/MonComptePage.js';
import { NotificationCenter } from './shell/NotificationCenter.js';
import { AncienHub, PlusPage } from './shell/PlusPage.js';
import { AppShell } from './shell/Shell.js';
import { ANCIENNES_PAGES, GESTION } from './shell/navigation.js';

/**
 * Interface web d'Oasis (spec 002, US1) : session OIDC, coque, accueil par rôle et écrans
 * des modules. Les droits sont toujours vérifiés par le serveur ; un écran non autorisé
 * affiche le refus renvoyé par l'API.
 */

function AccountRoute(): ReactElement {
  const { me, config, user } = useSession();
  return (
    <>
      <AccountPage me={me} config={config} />
      <WebPushSection user={user} />
    </>
  );
}

function ModulesRoute(): ReactElement {
  return <ModulesPage user={useSession().user} />;
}

function HealthRoute(): ReactElement {
  return <HealthPage user={useSession().user} />;
}

function NotFound(): ReactElement {
  return (
    <>
      <PageHeader title="Page introuvable" />
      <EmptyState title="Cette page n'existe pas ou n'est pas disponible pour votre profil.">
        <ButtonLink to="/">Retour à l'accueil</ButtonLink>
      </EmptyState>
    </>
  );
}

function RouteError(): ReactElement {
  const error = useRouteError();
  const morceau = estEchecChargementMorceau(error);
  useEffect(() => {
    // Écran d'une version retirée (onglet ouvert avant une mise en production) : rechargement
    // automatique une seule fois, sinon bandeau de mise à jour.
    if (morceau) echecChargementMorceau();
  }, [morceau]);
  if (morceau)
    return (
      <EmptyState title="Nouvelle version d'Oasis">
        <p>Cet écran a été mis à jour depuis l’ouverture de la page.</p>
        <Button variant="primary" onClick={recharger}>
          Recharger
        </Button>
      </EmptyState>
    );
  if (error instanceof ApiRequestError && error.status === 403)
    return (
      <EmptyState title="Accès refusé">
        <p>Votre profil ne permet pas d'ouvrir cet écran.</p>
        <ButtonLink to="/">Retour à l'accueil</ButtonLink>
      </EmptyState>
    );
  return <ErrorMessage error={error} />;
}

const queries = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Un refus ou une ressource absente ne se corrige pas en réessayant.
      retry: (count, error) =>
        !(error instanceof ApiRequestError && error.status >= 400 && error.status < 500) &&
        count < 2,
    },
  },
});

const router = createBrowserRouter([
  {
    element: (
      <AppShell
        notifications={
          <>
            <AideBouton />
            <NotificationCenter />
          </>
        }
      />
    ),
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/compte', element: <AccountRoute /> },
      // Deux espaces (révision du 2026-10-31) : « Plus » et Mon compte côté bénévole, accueil
      // et rubriques côté gestion ; aucune adresse d'écran ne change, les anciennes pages de
      // domaine mènent à la rubrique qui les remplace.
      { path: '/plus', element: <PlusPage /> },
      { path: '/mon-compte', element: <MonComptePage /> },
      { path: '/gestion', element: <GestionHome /> },
      ...GESTION.map((d) => ({ path: d.path, element: <DomainPage domaine={d.key} /> })),
      ...ANCIENNES_PAGES.map((path) => ({ path, element: <AncienHub /> })),
      { path: '/modules', element: <ModulesRoute /> },
      { path: '/sante', element: <HealthRoute /> },
      ...MODULE_WEB.flatMap((m) => m.routes).map((r) => ({
        ...r,
        element: <Suspense fallback={<Loading />}>{r.element}</Suspense>,
      })),
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export function App({
  config,
  users,
}: {
  config: RuntimeConfig;
  users: UserManager;
}): ReactElement {
  return (
    <QueryClientProvider client={queries}>
      <SessionProvider
        config={config}
        users={users}
        fallback={(error) =>
          error instanceof ApiRequestError && error.body?.code === 'maintenance' ? (
            <MaintenancePage />
          ) : (
            <main className="o-main">
              <ErrorMessage error={error} />
            </main>
          )
        }
      >
        <RouterProvider router={router} />
      </SessionProvider>
    </QueryClientProvider>
  );
}
